import PropTypes from 'prop-types'
import { Children } from 'react'
import Spinner from 'components/Spinner'
import styled from 'styled-components'
import { background as backgroundColor } from 'theme/palette'
import { borderRadius, borderWidth } from 'theme/globals'
import Box from '@mui/material/Box'

const VARIANTS = {
  tertiaryFilled: {
    radius: 'more',
    color: 'secondary_b500',
    size: 'shorter',
    fillBg: true,
  },
  tertiaryOutlined: {
    outlined: true,
    radius: 'more',
    color: 'secondary_b500',
    size: 'shorter',
  },
}

const PADDING = {
  tall: { pad: [1.5, 2.5], minHeight: '55px' },
  shorter: { pad: [0.7, 1.5], minHeight: null },
  short: { pad: [1, 2], minHeight: null },
  mini: { pad: [0.5, 1.5], minHeight: null },
  default: { pad: [1.1, 2], minHeight: '46px' },
}

const StyledButton = styled.button.withConfig({
  shouldForwardProp: prop => ![
    'font',
    'color',
    'fontColor',
    'bgColor',
    'borderColor',
    'borderColorDisabled',
    'outlined',
    'fillBg',
    'borderWidth',
    'fluid',
    'size',
    'pad',
    'onDisabledClick',
    'justifyContent',
    'radius',
    'disabled',
    'isDisabled',
    'isLoading',
    'variant',
  ].includes(prop),
})`
  ${props => props.theme.typography[props.font]}
  ${props => props.theme.internalMixins.p(props.pad || PADDING[props.size]?.pad || [0])}
  ${props => (props.fluid ? `width: 100%;` : '')}
  ${props => PADDING[props.size]?.minHeight ? `min-height: ${PADDING[props.size]?.minHeight};` : ''}
  display: inline-grid;
  grid-auto-flow: column;
  grid-gap: 10px;
  align-items: center;
  justify-content: center;
  text-align: center;
  text-decoration: none;
  border-radius: ${props => props.theme.globals.borderRadius[props.radius]}px;
  -webkit-touch-callout: none;
  user-select: none;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  background: ${props => props.outlined ? 'transparent' : props.theme.palette.background[props.color]?.main};
  border-style: solid;
  border-color: ${props => props.theme.palette.background[props.color]?.borderColor || props.theme.palette.background[props.color]?.main};
  border-width: ${props => props.theme.globals.borderWidth[props.borderWidth]};
  color: ${props => props.outlined ? props.theme.palette.background[props.color]?.main : props.theme.palette.background[props.color]?.text};
  transition: ${props => props.theme.state.transition.main};

  > span {
    display: inherit;
    grid-auto-flow: inherit;
    grid-gap: inherit;
    align-items: inherit;
    justify-content: inherit;
    text-align: inherit;
  }

  svg {
    transition: none !important;
  }

  ${props => props.isDisabled ? `
    border-color: ${props.theme.palette.border[props.borderColorDisabled] || props.theme.palette.background.disabled.main};
    background: ${props.outlined ? 'transparent' : props.theme.palette.background.disabled.main};
    color: ${props.theme.palette.background.disabled.text};
  ` : `
    &:hover,
    &:focus {
      color: ${props.outlined ? props.theme.palette.background[props.color]?.text : props.theme.palette.background[props.color]?.main};

      ${props.fillBg ? `
        background: ${props.theme.palette.background.main.main};
      ` : `
        background: ${props.outlined ? props.theme.palette.background[props.color]?.main : 'transparent'};
      `
      }
    }

    &:active {
      color: ${props.outlined ? props.theme.palette.background[props.color]?.text : props.theme.palette.background[props.color]?.text};

      ${props.fillBg ? `background: ${props.theme.palette.background.main.main};` : `
        background: ${props.outlined ? props.theme.palette.background[props.color]?.main : 'transparent'};
      `
      }
    }
  `}
`

const Button = ({
  variant,
  children,
  justifyContent,
  disabled,
  isLoading,
  onDisabledClick,
  onClick,
  onMouseUp,
  onMouseDown,
  ...props
}) => {
  const memoProps = variant ? ({ ...props, ...VARIANTS[variant] }) : props
  // const memoProps = useMemo(() => variant ? ({ ...props, ...VARIANTS[variant] }) : props, [variant])
  const disabledClick = isLoading || disabled

  const handleOnKeyUp = e => {
    if (e.keyCode === 13) {
      if (disabledClick) {
        onDisabledClick?.(e)
      } else {
        onClick?.(e)
        onMouseUp?.(e)
        onMouseDown?.(e)
      }
    }
  }

  return (
    <Box   
      display="flex" 
      justifyContent={justifyContent}
      width={memoProps.fluid ? '100%' : 'auto'}
    >
      <StyledButton
        className="tabbable"
        isDisabled={disabledClick}
        onClick={e => (disabledClick ? onDisabledClick?.(e) : onClick?.(e))}
        onMouseUp={e => !disabledClick && onMouseUp?.(e)}
        onMouseDown={e => !disabledClick && onMouseDown?.(e)}
        onKeyUp={handleOnKeyUp}
        {...memoProps}
      >
        {isLoading && (
          <Box
            sx={theme => ({
              ...theme.internalMixins.mixinCenter,
              '& + span': {
                opacity: 0,
              }
            })}
          >
            <Spinner 
              position="absolute" 
              size="sm" 
              color="text.inverse"
            />
          </Box>
        )}
        <span style={{ transform: 'translateY(-.1em)' }}>{Children.toArray(children)}</span>
      </StyledButton>
    </Box>
  )
}

Button.propTypes = {
  font: PropTypes.oneOf(['button', 'buttonLarge', 'body', 'data', 'dataSmall']),
  href: PropTypes.string,
  color: PropTypes.oneOf(Object.keys(backgroundColor)),
  fontColor: PropTypes.string,
  bgColor: PropTypes.string,
  borderColor: PropTypes.string,
  borderColorDisabled: PropTypes.string,
  outlined: PropTypes.bool,
  fillBg: PropTypes.bool,
  borderWidth: PropTypes.oneOf(Object.keys(borderWidth)),
  fluid: PropTypes.bool,
  size: PropTypes.oneOf(['tall', 'default', 'short', 'shorter', 'mini']),
  pad: PropTypes.array,
  onClick: PropTypes.func,
  onMouseUp: PropTypes.func,
  onMouseDown: PropTypes.func,
  onDisabledClick: PropTypes.func,
  justifyContent: PropTypes.string,
  radius: PropTypes.oneOf(Object.keys(borderRadius)),
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  variant: PropTypes.oneOf(Object.keys(VARIANTS)),
}

Button.defaultProps = {
  font: 'button',
  color: 'primary',
  fontColor: 'primary',
  bgColor: 'primary',
  borderColor: 'primary',
  outlined: false,
  borderWidth: 'thick',
  size: 'default',
  radius: 'main',
}

export default Button
